import React, { useMemo } from "react";
import { Box, Grid, Typography } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import {
  StatCard,
  CardHeading,
  CardSecondaryText,
  cardStatIconSx,
  formatNumber,
} from "./MediaKitComponents";
import { MediaKitAnalyticsLayout } from "./MediaKitAnalyticsLayout";
import { MediaKitDemographyTextBlock } from "./MediaKitDemographyTextBlock";
import type { PodcastMediaKit as PodcastMediaKitType } from "../../api/podcasts";
import { useTranslation } from "react-i18next";
import { MediaKitMetricsWindowCaption } from "./MediaKitMetricsWindowCaption";

interface MediaKitTikTokAnalyticsSectionProps {
  analytics: PodcastMediaKitType["analytics"];
  platformUrl?: string | null;
  contentCount?: number;
}

export const MediaKitTikTokAnalyticsSection: React.FC<
  MediaKitTikTokAnalyticsSectionProps
> = ({ analytics, platformUrl: _platformUrl, contentCount }) => {
  const { t } = useTranslation("mediaKit");
  const tiktokConsumptionTotalViews = useMemo(() => {
    if (!analytics?.tiktok?.consumption?.totalViews) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <VisibilityIcon sx={cardStatIconSx(30)} />
          <CardHeading variant="h5">
            {formatNumber(analytics.tiktok.consumption.totalViews)}
          </CardHeading>
          <CardSecondaryText variant="body2">{t("metrics.totalViews")}</CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [analytics?.tiktok?.consumption?.totalViews, t]);

  const tiktokConsumptionAvgViews = useMemo(() => {
    if (!analytics?.tiktok?.consumption?.avgViewsPerContent) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <EqualizerIcon sx={cardStatIconSx(30)} />
          <CardHeading variant="h5">
            {formatNumber(analytics.tiktok.consumption.avgViewsPerContent)}
          </CardHeading>
          <CardSecondaryText variant="body2">{t("metrics.avgViews")}</CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [analytics?.tiktok?.consumption?.avgViewsPerContent, t]);

  const tiktokConsumptionTotalInteractions = useMemo(() => {
    if (!analytics?.tiktok?.consumption?.totalInteractions) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <TouchAppIcon sx={cardStatIconSx(30)} />
          <CardHeading variant="h5">
            {formatNumber(analytics.tiktok.consumption.totalInteractions)}
          </CardHeading>
          <CardSecondaryText variant="body2">
            {t("metrics.totalInteractions")}
          </CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [analytics?.tiktok?.consumption?.totalInteractions, t]);

  const tiktokConsumptionAvgInteractions = useMemo(() => {
    if (!analytics?.tiktok?.consumption?.avgInteractions) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <TouchAppIcon sx={cardStatIconSx(30)} />
          <CardHeading variant="h5">
            {formatNumber(analytics.tiktok.consumption.avgInteractions)}
          </CardHeading>
          <CardSecondaryText variant="body2">
            {t("metrics.avgInteractions")}
          </CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [analytics?.tiktok?.consumption?.avgInteractions, t]);

  const tiktokEngagementRate = useMemo(() => {
    if (!analytics?.tiktok?.engagement?.engagementRate) return null;
    return (
      <Grid size={{ xs: 12, sm: 6, md: 4 }}>
        <StatCard>
          <ThumbUpIcon sx={cardStatIconSx(30)} />
          <CardHeading variant="h5">
            {analytics.tiktok.engagement.engagementRate.toFixed(2)}%
          </CardHeading>
          <CardSecondaryText variant="body2">
            {t("metrics.engagementRate")}
          </CardSecondaryText>
        </StatCard>
      </Grid>
    );
  }, [analytics?.tiktok?.engagement?.engagementRate, t]);

  if (!analytics?.tiktok) {
    return null;
  }

  const tkCons = analytics.tiktok.consumption;
  const hasTiktokConsumo =
    tkCons && (tkCons.totalViews != null || tkCons.avgViewsPerContent != null);
  const hasTiktokEngagementSection =
    analytics.tiktok.engagement?.engagementRate != null ||
    tkCons?.totalInteractions != null ||
    tkCons?.avgInteractions != null;

  const aud = analytics.tiktok.audience;
  const hasTiktokAudienceSection = Boolean(
    aud &&
    ((aud.gender && aud.gender.length > 0) ||
      (aud.ageRanges && aud.ageRanges.length > 0)),
  );

  if (
    !hasTiktokConsumo &&
    contentCount == null &&
    !hasTiktokEngagementSection &&
    !hasTiktokAudienceSection
  ) {
    return null;
  }

  return (
    <MediaKitAnalyticsLayout>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {(hasTiktokConsumo || contentCount != null) && (
          <Box component="section" data-mediakit-pdf-section="" sx={{ pb: 4 }}>
            <CardHeading variant="h5" sx={{ mb: 2 }}>
              {t("metrics.consumption")}
            </CardHeading>
            <Grid container spacing={2}>
              {contentCount != null && (
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <StatCard>
                    <OndemandVideoIcon sx={cardStatIconSx(30)} />
                    <CardHeading variant="h5">{contentCount}</CardHeading>
                    <CardSecondaryText variant="body2">
                      {t("metrics.videos")}
                    </CardSecondaryText>
                  </StatCard>
                </Grid>
              )}
              {tiktokConsumptionTotalViews}
              {tiktokConsumptionAvgViews}
            </Grid>
            <MediaKitMetricsWindowCaption
              format="shorts"
              inProgressCount={tkCons?.inProgressCount}
            />
          </Box>
        )}

        {hasTiktokEngagementSection && (
          <Box component="section" data-mediakit-pdf-section="" sx={{ pb: 4 }}>
            <CardHeading variant="h5" sx={{ mb: 2 }}>
              Engagement
            </CardHeading>
            <Grid container spacing={2}>
              {tiktokConsumptionTotalInteractions}
              {tiktokConsumptionAvgInteractions}
              {tiktokEngagementRate}
            </Grid>
          </Box>
        )}
      </Box>

      {hasTiktokAudienceSection && aud && (
        <Box component="section" data-mediakit-pdf-section="" sx={{ pb: 4 }}>
          <MediaKitDemographyTextBlock
            gender={aud.gender}
            ageRanges={aud.ageRanges}
            showSelfReportedNote={aud.selfReported === true}
          />
        </Box>
      )}
    </MediaKitAnalyticsLayout>
  );
};
